// The "View ticket" link in the Kuika ITSM's mails: `https://<designer>/helpdesk?ticket={ticketNumber}`
// (the ITSM's TicketLinkTemplate). The ticket is remembered for the tab before anything can redirect,
// so a signed-out user who goes through the login page still lands on it afterwards.

export const HELPDESK_SEGMENT: string = "helpdesk";
export const HELPDESK_TICKET_PARAM: string = "ticket";
const PENDING_KEY: string = "kuika.helpdeskPendingTicket";
const TICKET_NUMBER_PATTERN: RegExp = /^[1-9]\d{0,14}$/;

interface ILocationLike {
  pathname: string;
  search: string;
}

/** A ticket number from the address, or null. Any positive integer: numbers have no fixed width. */
export function parseTicketNumber(raw: string | null | undefined): number | null {
  const value: string = (raw ?? "").trim();
  return TICKET_NUMBER_PATTERN.test(value) ? Number(value) : null;
}

/** `/helpdesk` or `/{workspace}/helpdesk`. */
export function isHelpdeskPath(pathname: string): boolean {
  const segments: string[] = pathname.split("/").filter((segment: string): boolean => segment.length > 0);
  return segments.length >= 1 && segments.length <= 2 && segments[segments.length - 1] === HELPDESK_SEGMENT;
}

export function helpdeskPath(domainName: string | null | undefined, ticketNumber?: number | null): string {
  const base: string = domainName ? `/${encodeURIComponent(domainName)}/${HELPDESK_SEGMENT}` : `/${HELPDESK_SEGMENT}`;
  return ticketNumber ? `${base}?${HELPDESK_TICKET_PARAM}=${ticketNumber}` : base;
}

function sessionStore(): Storage | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** Run once at start-up, before the router can redirect to the login page. */
export function captureHelpdeskDeepLink(location: ILocationLike): void {
  if (!isHelpdeskPath(location.pathname)) return;
  const ticketNumber: number | null = parseTicketNumber(
    new URLSearchParams(location.search).get(HELPDESK_TICKET_PARAM)
  );
  if (ticketNumber === null) return;
  try {
    sessionStore()?.setItem(PENDING_KEY, String(ticketNumber));
  } catch {
    // Storage blocked: the link still works for a user who is already signed in.
  }
}

export function readPendingHelpdeskTicket(): number | null {
  try {
    return parseTicketNumber(sessionStore()?.getItem(PENDING_KEY));
  } catch {
    return null;
  }
}

export function clearPendingHelpdeskTicket(): void {
  try {
    sessionStore()?.removeItem(PENDING_KEY);
  } catch {
    // Nothing to clear.
  }
}
